import { Link, useSearchParams } from 'react-router-dom';
import { Gate } from '../../../global/contexts/auth/auth';
import { Tag } from '../../../common/components/detail/detail';
import { useHistory, useSnapshot } from '../contexts/coordination';
import { onFixtures } from '../queries/source';
import { VERB_LABEL, label } from '../utils/format';
import {
    Fact,
    Facts,
    Moment,
    Origin,
    RawJson,
    RefreshStatus,
    Separations,
    TechnicalDetails,
    Unknown,
} from './bits';

// HISTORIAL — qué pasó, y la reconstrucción.
//
// El cursor es `events.id`: el mismo número que `Last-Event-ID` del canal en
// vivo. Hay un solo registro y una sola numeración.
//
// LAS DOS LECTURAS VAN JUNTAS. La instantánea dice qué hay ahora y por qué
// número seguir; el historial dice qué pasó desde ese número. Pedir una sin la
// otra deja un hueco o cuenta dos veces lo mismo — por eso esta pantalla las
// muestra en el mismo lugar.
//
// Y lo que pasó no es en qué quedó: `happened` es lo que el evento registró
// aquel día, `outcome` es la fila de hoy.

const EVENT_LABELS = {
    'control.requested': 'Cambio de disponibilidad solicitado',
    'orders.issued': 'Orden registrada',
    'orders.dispatched': 'Orden enviada al equipo',
    'orders.acknowledged': 'El equipo recibió la orden',
    'orders.started': 'El equipo inició la orden',
    'orders.reported': 'El equipo informó el resultado',
    'orders.state_changed': 'Cambió el estado de una orden',
    'task.claimed': 'Tarea reservada',
    'task.taken': 'Tarea tomada',
    'task.progress': 'Avance registrado',
    'task.released': 'Reserva liberada',
    'task.blocked': 'Tarea bloqueada',
    'task.unblocked': 'Tarea desbloqueada',
    'attempts.opened': 'Ejecución iniciada',
    'attempts.finished': 'Ejecución terminada',
    'attempts.expired': 'Ejecución vencida',
    'positions.registered': 'Puesto registrado',
    'positions.state_changed': 'Cambió el estado de un puesto',
    'machines.registered': 'Computador registrado',
    'machines.state_changed': 'Cambió el estado de un computador',
    'task.delivered': 'Tarea entregada',
    'task.accepted': 'Entrega aceptada',
    'task.returned': 'Tarea devuelta',
    'review.evaluated': 'Evaluación registrada',
    'review.decided': 'Revisión decidida',
};

export default function HistoryScreen() {
    const [params, setParams] = useSearchParams();
    const after = params.get('despues') || '0';
    const family = params.get('familia') || '';
    const history = useHistory({ after, family });
    const snapshot = useSnapshot();
    const change = (key, value) => {
        const next = new URLSearchParams(params);
        value ? next.set(key, value) : next.delete(key);
        if (key === 'familia') next.delete('despues');
        setParams(next);
    };
    return (
        <section className="coord">
            <header className="coord__head">
                <h2 className="coord__title">Actividad de los agentes</h2>
            </header>
            <p className="coord__about">
                Órdenes, ejecuciones y revisiones, en orden de ocurrencia.
            </p>
            <div className="coord__toolbar">
                <label className="coord__field">
                    <span>Mostrar</span>
                    <select
                        className="coord__select"
                        value={family}
                        disabled={onFixtures}
                        onChange={e => change('familia', e.target.value)}
                    >
                        <option value="">Toda la actividad</option>
                        <option value="orden">Órdenes</option>
                        <option value="ejecucion">Ejecuciones</option>
                        <option value="revision">Revisiones</option>
                    </select>
                </label>
                <RefreshStatus query={history} />
            </div>
            {history.isLoading && (
                <p role="status" className="coord__loading">
                    Consultando actividad…
                </p>
            )}
            {history.error && (
                <p role="alert" className="coord__error">
                    {history.error.message}
                </p>
            )}
            {history.data && (
                <>
                    {history.data.page.reload && (
                        <p className="coord__unavailable">
                            El punto de lectura quedó desactualizado. Consulta el estado actual y
                            reanuda desde allí.
                        </p>
                    )}
                    <HistoryPage data={history.data} />
                    <nav className="coord__page-controls" aria-label="Paginación de actividad">
                        <button
                            type="button"
                            className="coord__button"
                            disabled={after === '0' || history.isFetching}
                            onClick={() => change('despues', '')}
                        >
                            Volver al inicio
                        </button>
                        <button
                            type="button"
                            className="coord__button"
                            disabled={
                                onFixtures || !history.data.page.has_more || history.isFetching
                            }
                            onClick={() =>
                                change('despues', history.data.page.resume_from_event_id)
                            }
                        >
                            Siguientes eventos
                        </button>
                    </nav>
                </>
            )}
            <TechnicalDetails label="Estado actual y recuperación de la conexión">
                <Origin origin={snapshot.origin} />
                {snapshot.error && <p className="coord__error">{snapshot.error.message}</p>}
                {snapshot.data && (
                    <>
                        <Snapshot data={snapshot.data} />
                        <button
                            type="button"
                            className="coord__button"
                            onClick={() => change('despues', snapshot.data.resume_from_event_id)}
                        >
                            Ver actividad posterior a esta lectura
                        </button>
                    </>
                )}
            </TechnicalDetails>
        </section>
    );
}

function Snapshot({ data }) {
    const blocks = [
        ['Reservas vivas', data.pending.claims, claimRow],
        ['Corridas abiertas', data.pending.attempts, attemptRow],
        ['Órdenes vivas', data.pending.orders, orderRow],
        ['Rondas sin decidir', data.pending.rounds, roundRow],
    ].filter(([, block]) => block);

    return (
        <>
            <p className="coord__panel-note">
                Seguir desde el evento <strong>#{data.resume_from_event_id}</strong>. La cabeza del
                registro se lee ANTES que las filas: lo que pase en el medio queda por delante del
                cursor y vuelve a llegar, en vez de perderse.
            </p>

            <div className="coord__snapshot">
                {blocks.map(([title, block, row]) => (
                    <section key={title} className="coord__snapshot-block">
                        <header>
                            <strong>{title}</strong>
                            <span>
                                {block.total} en total · se listan {block.items.length}
                            </span>
                        </header>
                        {block.resume_after != null && (
                            <p className="coord__panel-note">
                                Quedaron más: seguir con <code>{block.resume_after}</code>. El
                                límite recorta la RELECTURA, nunca una decisión pendiente.
                            </p>
                        )}
                        <ul>{block.items.slice(0, 6).map((item, index) => row(item, index))}</ul>
                        {block.items.length > 6 && (
                            <p className="coord__panel-note">
                                y {block.items.length - 6} más en la respuesta.
                            </p>
                        )}
                        {!block.items.length && <p className="coord__panel-note">Ninguna.</p>}
                    </section>
                ))}
            </div>

            <RawJson value={data} label="Ver la instantánea como llegó" />
        </>
    );
}

function claimRow(item, key) {
    return (
        <li key={key}>
            <span>tarea #{item.task_id}</span>
            <span>{item.workflow_state}</span>
            <span>
                reservada hasta <Moment at={item.claimed_until} />
            </span>
        </li>
    );
}

function attemptRow(item, key) {
    return (
        <li key={key}>
            <span>
                corrida #{item.attempt_id} · tarea #{item.task_id}
            </span>
            <span>
                {item.seat?.machine_name} · puesto {item.position_id}
            </span>
            <span>
                {item.state}
                {item.lease_expired ? (
                    <Tag tone="warn">vigencia vencida</Tag>
                ) : (
                    <Tag tone="active">vigente</Tag>
                )}
            </span>
        </li>
    );
}

function orderRow(item, key) {
    return (
        <li key={key}>
            <span>
                orden #{item.order_id} · {label(VERB_LABEL, item.verb)}
            </span>
            <span>
                {item.seat?.machine_name} · puesto {item.position_id}
            </span>
            <span>
                {item.state}
                {item.acked_at ? '' : ' · sin acuse'}
                {item.expired && ' · vencida'}
            </span>
        </li>
    );
}

function roundRow(item, key) {
    return (
        <li key={key}>
            <span>
                ronda #{item.review_round_id ?? item.id} · tarea #{item.task_id}
            </span>
            <span>{item.state ?? ''}</span>
        </li>
    );
}

function HistoryPage({ data }) {
    return (
        <>
            <TechnicalDetails label="Detalles de la lectura">
                <Facts>
                    <Fact label="Seguir desde">#{data.page.resume_from_event_id}</Fact>
                    <Fact label="Hay más">{data.page.has_more ? 'sí' : 'no'}</Fact>
                    <Fact label="Atrasado">{data.page.behind} evento(s)</Fact>
                    <Fact label="Releer todo">
                        {data.page.reload ? `sí · ${data.page.reason}` : 'no'}
                    </Fact>
                </Facts>
            </TechnicalDetails>

            {data.items.length === 0 && (
                <p className="coord__empty">
                    Esta página no trae entradas.
                    {onFixtures && (
                        <>
                            {' '}
                            El escenario del fixture no dejó eventos en la ventana pedida: el
                            historial <strong>existe y contesta</strong>, pero acá no hay nada que
                            mostrar. Contra un Core con actividad, cada entrada trae su actor
                            resuelto —persona, credencial, equipo y puesto— y el recurso que nombra.
                        </>
                    )}
                </p>
            )}

            <ul className="coord__events">
                {data.items.map(item => (
                    <li key={item.id ?? item.event_id}>
                        <span className="coord__event-id">#{item.id ?? item.event_id}</span>
                        <strong className="coord__event-kind">
                            {EVENT_LABELS[item.kind] ?? item.kind ?? item.family}
                        </strong>
                        <span>
                            {item.actor?.user?.name ??
                                item.actor?.machine?.name ??
                                item.actor?.token?.name ??
                                'Sistema'}
                        </span>
                        <Moment at={item.at} />
                        {item.subject?.task && (
                            <Gate domain="tasks:read">
                                <Link to={'/admin/tasks/' + item.subject.task.id}>
                                    #{item.subject.task.id} · {item.subject.task.title}
                                </Link>
                            </Gate>
                        )}
                        <RawJson value={item} label="Ver detalle del evento" />
                    </li>
                ))}
            </ul>

            <TechnicalDetails>
                <Facts>
                    <Fact label="Tope de página">{data.limits.max_limit}</Fact>
                    <Fact label="Atraso máximo">{data.limits.max_behind}</Fact>
                    <Fact label="Filtros">
                        {Object.entries(data.filters).some(([, v]) => v != null) ? (
                            Object.entries(data.filters)
                                .filter(([, v]) => v != null)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(' · ')
                        ) : (
                            <Unknown>sin filtrar</Unknown>
                        )}
                    </Fact>
                </Facts>

                <Separations items={data.separations} />
                <RawJson value={data} label="Ver el historial como llegó" />
            </TechnicalDetails>
        </>
    );
}
